import { useEffect, useState } from "react";

import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";

import { useToast } from "~/hooks/use-toast";
import { useCurrentUser } from "~/hooks/useCurrentUser";
import {
  fetchAllFacturasOptimized,
  useOptimizedFacturasService,
} from "~/services/facturasOptimizedService";
import { fetchAllFacturas } from "~/services/salesRequestDocumentService";

/**
 * Optimized hook for fetching facturas using database views
 * Falls back to original implementation based on feature flag
 */
export const useFacturasOptimized = (forceOptimized: boolean = false) => {
  const { user } = useCurrentUser();
  const { toast } = useToast();
  const enableOptimized = forceOptimized || useOptimizedFacturasService();

  // Filter dates (what the user selects in the UI) - Default to current month start to today
  const [startDate, setStartDate] = useState<Date | undefined>(
    dayjs().startOf("month").toDate()
  );
  const [endDate, setEndDate] = useState<Date | undefined>(dayjs().toDate());

  // Validate dates before using them in the query
  const validStartDate =
    startDate instanceof Date && !isNaN(startDate.getTime())
      ? startDate
      : undefined;
  const validEndDate =
    endDate instanceof Date && !isNaN(endDate.getTime()) ? endDate : undefined;

  // Use React Query for data fetching with automatic deduplication and caching
  const {
    data: facturas = [],
    isLoading: loading,
    error: queryError,
    refetch,
  } = useQuery({
    queryKey: [
      enableOptimized ? "facturas-optimized" : "facturas",
      validStartDate?.toISOString(),
      validEndDate?.toISOString(),
      user.role,
      user.id,
      enableOptimized ? "v2" : "v1", // Version flag for cache differentiation
    ],
    queryFn: async () => {
      try {
        console.log(
          `🔄 FETCH: Using ${enableOptimized ? "OPTIMIZED" : "ORIGINAL"} service`
        );

        let result;
        if (enableOptimized) {
          result = await fetchAllFacturasOptimized(
            validStartDate,
            validEndDate,
            user.role || undefined,
            user.id || undefined
          );
        } else {
          result = await fetchAllFacturas(
            validStartDate,
            validEndDate,
            user.role || undefined,
            user.id || undefined
          );
        }

        return result;
      } catch (error) {
        throw error;
      }
    },
    enabled: !!(user.id && user.role), // Only run query when user is loaded
    staleTime: enableOptimized ? 1000 * 60 * 10 : 1000 * 60 * 5, // Optimized version can be cached longer
    gcTime: enableOptimized ? 1000 * 60 * 60 : 1000 * 60 * 30, // Optimized version cached for 1 hour vs 30 minutes
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });

  // Handle errors with toast notifications
  const error = queryError?.message || null;

  useEffect(() => {
    if (queryError) {
      console.error("Error fetching facturas:", queryError);
      toast({
        title: "Error",
        description:
          "No se pudieron cargar las facturas. Por favor, intenta nuevamente.",
        variant: "destructive",
      });
    }
  }, [queryError, toast]);

  // Function to refresh the facturas (triggers React Query refetch)
  const refreshFacturas = () => {
    console.log(
      `🔄 REFRESH: Refreshing facturas using ${enableOptimized ? "OPTIMIZED" : "ORIGINAL"} service`
    );
    refetch();
  };

  // Log performance information
  useEffect(() => {
    if (!loading && facturas.length > 0) {
      console.log(
        `⚡ PERFORMANCE: Loaded ${facturas.length} facturas using ${enableOptimized ? "OPTIMIZED" : "ORIGINAL"} service`
      );
    }
  }, [loading, facturas.length, enableOptimized]);

  return {
    facturas,
    loading,
    error,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    refreshFacturas,
    isOptimized: enableOptimized, // Expose optimization status for debugging
  };
};
